import { useEffect, useState, memo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Container,
  Chip,
  styled,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  Person,
  AccessTime
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { fetchBlogPostById } from '../../../services/aboutUsService';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#fafafa',
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8)
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  maxWidth: '900px',
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 0
  }
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: '#028383',
  '&:hover': {
    backgroundColor: 'rgba(2, 131, 131, 0.08)'
  }
}));

const CoverImage = styled('img')(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(2),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(4),
  maxHeight: '500px',
//   objectFit: 'cover',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
}));

const MetaBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  color: theme.palette.text.secondary,
  fontSize: '0.875rem'
}));

const ContentBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  '& p': {
    marginBottom: theme.spacing(2.5),
    lineHeight: 1.8,
    fontSize: '1.063rem',
    color: '#333'
  },
  '& h2': {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontWeight: 700,
    fontSize: '1.75rem',
    color: '#1a1a1a'
  },
  '& h3': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: '1.5rem',
    color: '#1a1a1a'
  },
  '& h4': {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: '1.25rem',
    color: '#1a1a1a'
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2.5),
    paddingLeft: theme.spacing(3),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.7
    }
  },
  '& blockquote': {
    borderLeft: '4px solid #028383',
    paddingLeft: theme.spacing(2),
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    fontStyle: 'italic',
    color: '#555'
  },
  '& a': {
    color: '#028383',
    textDecoration: 'none',
    borderBottom: '1px solid rgba(2, 131, 131, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderBottomColor: '#028383',
      color: '#026e6e'
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  '& code': {
    backgroundColor: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.9em',
    fontFamily: 'monospace'
  },
  '& pre': {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    overflow: 'auto',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '& code': {
      backgroundColor: 'transparent',
      padding: 0
    }
  }
}));

interface BlogPostData {
  fields: {
    title?: string;
    author?: string;
    categoryCard?: string;
  };
  sys: {
    createdAt: string;
  };
  resolvedTemplate?: {
    fields: {
      blogTitle?: string;
      description?: any;
    };
  };
  resolvedCoverImage?: {
    fields: {
      file: {
        url: string;
      };
    };
  };
}

const BlogReadingPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
  window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (id) {
      loadBlogPost();
    }
  }, [id]);

  const loadBlogPost = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    const result = await fetchBlogPostById(id);

    if (result.success) {
      setBlogPost(result.data);
    } else {
      setError(result.error || 'Failed to load blog post');
    }

    setLoading(false);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadTime = (content: any): string => {
    if (!content?.content) return '5 min read';
    
    let wordCount = 0;
    const countWords = (node: any) => {
      if (node.nodeType === 'text') {
        wordCount += node.value.split(/\s+/).length;
      }
      if (node.content) {
        node.content.forEach(countWords);
      }
    };
    
    content.content.forEach(countWords);
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  // Rich text rendering options
  const richTextOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_: any, children: any) => (
        <Typography component="p" paragraph>
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_2]: (_: any, children: any) => (
        <Typography variant="h2" component="h2">
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_3]: (_: any, children: any) => (
        <Typography variant="h3" component="h3">
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_4]: (_: any, children: any) => (
        <Typography variant="h4" component="h4">
          {children}
        </Typography>
      ),
      [BLOCKS.UL_LIST]: (_: any, children: any) => (
        <ul>{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (_: any, children: any) => (
        <ol>{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (_: any, children: any) => (
        <li>{children}</li>
      ),
      [BLOCKS.QUOTE]: (_: any, children: any) => (
        <blockquote>{children}</blockquote>
      ),
      [INLINES.HYPERLINK]: (node: any, children: any) => (
        <a 
          href={node.data.uri} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )
    }
  };

  // Loading State
  if (loading) {
    return (
      <PageContainer>
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh'
            }}
          >
            <CircularProgress size={48} sx={{ color: '#028383', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading article...
            </Typography>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  // Error State
  if (error || !blogPost) {
    return (
      <PageContainer>
        <Container maxWidth="md">
          <Box sx={{ py: 8 }}>
            <Alert 
              severity="error"
              sx={{ mb: 3 }}
            >
              {error || 'Blog post not found'}
            </Alert>
            <BackButton onClick={() => navigate(-1)}>
              <ArrowBack sx={{ mr: 1 }} />
              <Typography variant="button">Go Back</Typography>
            </BackButton>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  const template = blogPost.resolvedTemplate;

  if (!template || !template.fields.description) {
    return (
      <PageContainer>
        <Container maxWidth="md">
          <Box sx={{ py: 8 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              Blog content is not available or not linked properly.
            </Alert>
            <BackButton onClick={() => navigate(-1)}>
              <ArrowBack sx={{ mr: 1 }} />
              <Typography variant="button">Go Back</Typography>
            </BackButton>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  const title = blogPost.fields.title || template.fields.blogTitle || 'Untitled';
  const author = blogPost.fields.author || 'Team 4um';
  const category = blogPost.fields.categoryCard || 'Article';
  const publishDate = formatDate(blogPost.sys.createdAt);
  const readTime = calculateReadTime(template.fields.description);

  return (
    <PageContainer>
      <ContentContainer>
        {/* Back Button */}
        <Box style={{display:"flex",justifyContent:"space-between"}} >
        <BackButton onClick={() => navigate(-1)} style={{borderRadius:"100px"}}>
          <ArrowBack fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="button" sx={{ fontSize: '0.875rem' }}>
            Back to Insights
          </Typography>
        </BackButton>

        {/* Category Badge */}
        <Chip
          label={category}
          sx={{
            backgroundColor: '#028383',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.813rem',
            mb: 2
          }}
        />
        </Box>

        {/* Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#1a1a1a',
            mb: 2,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            lineHeight: 1.2
          }}
        >
          {title}
        </Typography>

        {/* Meta Information */}
        <MetaBox>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person sx={{ fontSize: '1.125rem' }} />
            <Typography variant="body2">{author}</Typography>
          </Box>
          <Typography variant="body2">•</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: '1rem' }} />
            <Typography variant="body2">{publishDate}</Typography>
          </Box>
        </MetaBox>

        <Divider sx={{ mb: 3 }} />

        {/* Cover Image */}
        {blogPost.resolvedCoverImage && (
          <CoverImage
            src={`https:${blogPost.resolvedCoverImage.fields.file.url}`}
            alt={title}
          />
        )}

        {/* Article Content */}
        <ContentBox>
          {documentToReactComponents(
            template.fields.description,
            richTextOptions
          )}
        </ContentBox>

        {/* Footer Divider */}
        <Divider sx={{ mt: 6, mb: 3 }} />

        {/* Back Button at Bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <BackButton onClick={() => navigate(-1)} style={{borderRadius:"100px"}}>
            <ArrowBack fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="button" sx={{ fontSize: '0.875rem' }}>
              Back to Insights
            </Typography>
          </BackButton>
        </Box>
      </ContentContainer>
    </PageContainer>
  );
};

export default memo(BlogReadingPage);